import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { Card, Col, Row, Image, Modal, Button, Spin, Grid, App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchOneGameAction, fetchGamesListAction, RootState, selectStore } from "../../../../utils";
import { MyButton, MyText } from "../../../../components";
import { StyledGameContainer } from "./style";
import { DndProvider, useDrop, useDrag } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import confetti from "canvas-confetti";
import { ItemTypes } from "../../../../utils/constants/games";
import { PlayCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {LeftOutlined} from "@ant-design/icons";
import {ButtonContainer} from "../../style.ts";
import { LogoIcon } from "../../../../assets";
import { useEmbeddedNavigate } from "../../../../utils/context/embed/useEmbed.ts";
import { Helmet } from "react-helmet";

const getImageSize = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({ width: 130, height: 100 });
        img.src = url;
    });
};

const ChoiceImage = ({
    src,
    index,
    onSelect,
    isSelected,
}: {
    src: string;
    index: number;
    onSelect: (img: { src: string; index: number }) => void;
    isSelected: boolean;
}) => {
    const {useBreakpoint} = Grid;
    const screens = useBreakpoint();

    return (
        <div
            onClick={() => onSelect({ src, index })}
            style={{
                cursor: "pointer",
                width: screens.xs ? 130 : 230 ,
                height: screens.xs ? 100 : 200,
                padding: 0,
                margin: 0,
                border: isSelected ? "3px solid #1890ff" : 'none',
                borderRadius: 4,
                backgroundColor: isSelected ? "rgba(24, 144, 255, 0.1)" : "transparent",
                transform: isSelected ? "scale(0.95)" : "scale(1)",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                WebkitTapHighlightColor: "transparent",
                userSelect: "none",
            }}
        >
            <Image
                src={src}
                preview={false}
                width={screens.xs ? 130 : 230}
                height={screens.xs ? 100 : 200}
                style={{
                    pointerEvents: "none",
                    display: "block",
                    objectFit: "contain",
                }}
            />
        </div>
    );
};

const isMobile = window.innerWidth < 768;

// Определяем поддержку touch events
const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Выбираем правильный backend для DnD
const getDndBackend = () => {
    return isTouchDevice() ? TouchBackend : HTML5Backend;
};

const getDndOptions = () => {
    if (isTouchDevice()) {
        return {
            enableMouseEvents: true,
            enableTouchEvents: true,
            enableKeyboardEvents: false,
            delayTouchStart: 200,
            touchSlop: 10,
            ignoreContextMenu: true,
            scrollAngleRanges: [
                { start: 30, end: 150 },
                { start: 210, end: 330 }
            ]
        };
    }
    return {};
};

const PuzzlePiece = ({ piece, onSelect, isSelected }: { 
    piece: { id: number; url: string; width?: number; height?: number }, 
    onSelect?: (piece: any) => void,
    isSelected?: boolean
}) => {
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: "PUZZLE_PIECE",
        item: { id: piece.id, url: piece.url, width: piece.width, height: piece.height },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [piece]);

    return (
        <div
            ref={drag}
            onClick={onSelect ? () => onSelect(piece) : undefined}
            style={{
                opacity: isDragging || isSelected ? 0.7 : 1,
                cursor: "pointer",
                width: piece.width || 100,
                height: piece.height || 100,
                padding: 0,
                margin: 0,
                touchAction: "manipulation",
                border: isSelected ? "3px solid #1890ff" : "none",
                borderRadius: 4,
                transition: "all 0.2s ease",
                backgroundColor: isSelected ? "rgba(24, 144, 255, 0.1)" : "transparent",
                transform: isSelected ? "scale(0.95)" : "scale(1)",
                boxSizing: "border-box",
                WebkitTapHighlightColor: "transparent",
                userSelect: "none",
            }}
        >
            <Image 
                src={piece.url} 
                preview={false} 
                style={{ 
                    width: "100%", 
                    height: "100%", 
                    pointerEvents: "none",
                    display: "block",
                    objectFit: "contain" 
                }} 
            />
        </div>
    );
};

// Компонент ячейки пазла (drop-зона) без границы
const PuzzleCell = ({
    target,
    placedPiece,
    onDropPiece,
    onClick,
    isTargetCell,
}: {
    target: { id: number; coord: { x: number; y: number }; url: string; width?: number; height?: number };
    placedPiece: { id: number; url: string; width?: number; height?: number } | null;
    onDropPiece: (item: { id: number; url: string; width?: number; height?: number }) => void;
    onClick?: () => void;
    isTargetCell?: boolean;
}) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: "PUZZLE_PIECE",
        drop: (item: any) => {
            console.log("DROP: ", item);
            onDropPiece(item);
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [onDropPiece]);

    return (
        <div
            ref={drop}
            onClick={onClick}
            style={{
                position: "absolute",
                left: target?.coord?.x || 0,
                top: target?.coord?.y || 0,
                width: target?.width || 130,
                height: target?.height || 100,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: isOver ? "rgba(24, 144, 255, 0.1)" : "transparent",
                border: isOver ? "2px solid #1890ff" : "none",
                borderRadius: 0,
                transition: "all 0.2s ease",
                cursor: "pointer",
                boxSizing: "border-box",
                padding: 0,
                margin: 0,
            }}
        >
            {placedPiece && (
                <Image 
                    src={placedPiece.url} 
                    preview={false} 
                    style={{ 
                        width: "100%", 
                        height: "100%", 
                        pointerEvents: "none",
                        display: "block",
                        objectFit: "contain"
                    }} 
                />
            )}
        </div>
    );
};

export const CurrentGame = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const embeddedNavigate = useEmbeddedNavigate();

    const { t } = useTranslation("common");
    const { message } = App.useApp();

    const { materials: { oneGame: oneGameState, gamesList: gamesListState } }: RootState = useSelector(selectStore);
    const { age, selectedMaterial, currentGame, gameCategory } = useParams();
    const [searchParams] = useSearchParams();

    const [droppedImage, setDroppedImage] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [counter, setCounter] = useState(5);
    const [hasPlayedAudio, setHasPlayedAudio] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [placedPieces, setPlacedPieces] = useState<any[]>([]);
    const [availablePieces, setAvailablePieces] = useState<any[]>([]);
    const [isMobileDevice, setIsMobileDevice] = useState(window.innerWidth < 768);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [selectedChoice, setSelectedChoice] = useState<{ src: string; index: number } | null>(null);
    const [droppedImages, setDroppedImages] = useState<{ [key: number]: string }>({});
    const audioRef = useRef<HTMLAudioElement>(null);
    const correctSoundRef = useRef<HTMLAudioElement>(null);
    const wrongSoundRef = useRef<HTMLAudioElement>(null);

    const correctSoundUrl = "https://ff.bilimal.kz/ycosuyki/cwuagmcc/smyuagia/11d32a40a30f9374215e2e645944d22731571e35.wav";
    const wrongSoundUrl = "https://ff.bilimal.kz/ycosuyki/cwuagmcc/smyuagia/3b1e791b1823d17512302c81c5d231fe9ed631d5.wav";
    const soundOnIcon = "https://ff.bilimal.kz/ycosuyki/cwuagmcc/smyuagia/afb342c176c41917856443e58fff8b83db6b18c2.png";
    const soundOffIcon = "https://ff.bilimal.kz/ycosuyki/cwuagmcc/smyuagia/67da2109dc224a6f863a8022bf57bbd91cc104e1.png";

    // const startAudio = useCallback(() => {
    //     if (audioRef.current && !hasPlayedAudio && !isMuted) {
    //         const playPromise = audioRef.current.play();
    //         if (playPromise !== undefined) {
    //             playPromise
    //                 .then(() => {
    //                     setHasPlayedAudio(true);
    //                 })
    //                 .catch(() => {
    //                     setHasPlayedAudio(false);
    //                 });
    //         }
    //     }
    // }, [hasPlayedAudio, isMuted]);


    const startAudio = useCallback(() => {
        if (audioRef.current && !isMuted) {
            audioRef.current.currentTime = 0;
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setHasPlayedAudio(true);
                    })
                    .catch(() => {
                        setHasPlayedAudio(false);
                    });
            }
        }
    }, [isMuted]);

    const toggleMute = () => {
        const newMute = !isMuted;
        setIsMuted(newMute);
        if (audioRef.current) audioRef.current.muted = newMute;
        if (correctSoundRef.current) correctSoundRef.current.muted = newMute;
        if (wrongSoundRef.current) wrongSoundRef.current.muted = newMute;
    };

     const handleGoBack = () => {
        const backUrl = `/materials/detail/games-list/${gameCategory}?age=${age}&type=lessons&selectedMaterial=${selectedMaterial}`;
        embeddedNavigate(backUrl);
    }


    // Функция для перехода на следующую игру
    const goToNextGame = useCallback(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const ageParam = age ? `?age=${age}` : '';
        
        if (gamesListState?.data?.length && currentGame) {
            const currentIndex = gamesListState.data.findIndex((game: any) => game.id === Number(currentGame));
            if (currentIndex !== -1 && currentIndex < gamesListState.data.length - 1) {
                // Есть следующая игра
                const nextGame = gamesListState.data[currentIndex + 1];
                embeddedNavigate(`/materials/detail/games-list/${gameCategory}/age/${age}/selectedMaterial/${selectedMaterial}/game/${nextGame.id}`);
            } else {
                // Это последняя игра, возвращаемся к списку игр
                embeddedNavigate(`/materials/detail/games-list/${gameCategory}?age=${age}&type=lessons&selectedMaterial=${selectedMaterial}`);
            }
        } else {
            // Возвращаемся к списку игр
            embeddedNavigate(`/materials/detail/games-list/${gameCategory}?age=${age}&type=lessons&selectedMaterial=${selectedMaterial}`);
        }
    }, [gamesListState, currentGame, gameCategory, embeddedNavigate]);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        embeddedNavigate(`/materials/detail/games-list/${gameCategory}?age=${age}&type=lessons&selectedMaterial=${selectedMaterial}`);
    }, [embeddedNavigate]);

    const congratsText = [t("congratulations"), t("greatJob"), t("greatJob1")];

    useEffect(() => {
        startAudio();
    }, [startAudio, currentGame]);

    useEffect(() => {
        if (showModal) {
            setCounter(5);
        }
    }, [showModal]);

    useEffect(() => {
        if (counter > 0 && showModal) {
            const timer = setTimeout(() => setCounter(counter - 1), 1000);
            return () => clearTimeout(timer);
        } else if (counter === 0) {
            handleCloseModal();
        }
    }, [counter, showModal, handleCloseModal]);

    useEffect(() => {
        if (oneGameState?.data?.engine === "puzzleGame" && oneGameState.data.choiceImages) {
            const fetchSizes = async () => {
                const scaleFactor = window.innerWidth < 768 ? 0.45 : 0.75;
                const piecesWithSizes = await Promise.all(
                    oneGameState.data.choiceImages.map(async (piece: { id: number; url: string; coord: { x: number; y: number } }) => {
                        const { width, height } = await getImageSize(piece.url);
                        // const scaledWidth = Math.floor(width * scaleFactor);
                        // const scaledHeight = Math.floor(height * scaleFactor);
                        const scaledWidth = (width * scaleFactor);
                        const scaledHeight = (height * scaleFactor);
                        return { 
                            id: piece.id,
                            url: piece.url,
                            width: scaledWidth, 
                            height: scaledHeight,
                            coord: piece.coord ? {
                                // x: Math.floor(piece.coord.x * scaleFactor),
                                // y: Math.floor(piece.coord.y * scaleFactor)
                                x: (piece.coord.x * scaleFactor),
                                y: (piece.coord.y * scaleFactor)
                            } : { x: 0, y: 0 }
                        };
                    })
                );
                setPlacedPieces(Array(piecesWithSizes.length).fill(null));
                setAvailablePieces(piecesWithSizes);
            };
            fetchSizes();
        }
    }, [oneGameState]);

    const triggerFireworkConfetti = () => {
        const confettiCanvas = document.getElementById("confetti-canvas") as HTMLCanvasElement;
        if (confettiCanvas) {
            const myConfetti = confetti.create(confettiCanvas, { resize: true, useWorker: false });
            
            function randomInRange(min: number, max: number) {
                const secureRandom = () => {
                const array = new Uint32Array(1);
                crypto.getRandomValues(array);
                return array[0] / (0xFFFFFFFF + 1);
                };
                return secureRandom() * (max - min) + min;
            }

            // Массив из 7 различных фейерверков
            const fireworkTypes = [
                // 1. Золотой взрыв с сердечками
                () => {
                    myConfetti({
                        particleCount: 200,
                        spread: 180,
                        startVelocity: 50,
                        scalar: 1.5,
                        shapes: ['circle', 'square', 'star'],
                        colors: ['#FFD700', '#FFA500', '#FF6347', '#FFB6C1'],
                        origin: { x: 0.5, y: 0.5 },
                        ticks: 150,
                        zIndex: 9999
                    });
                    
                    // Сердечки вокруг
                    for (let i = 0; i < 8; i++) {
                        setTimeout(() => {
                            const angle = (i / 8) * Math.PI * 2;
                            myConfetti({
                                particleCount: 20,
                                spread: 30,
                                startVelocity: 25,
                                shapes: ['square', 'circle'],
                                colors: ['#FF1493', '#FF69B4', '#FFB6C1'],
                                origin: { 
                                    x: 0.5 + Math.cos(angle) * 0.3, 
                                    y: 0.5 + Math.sin(angle) * 0.3 
                                },
                                ticks: 80,
                                zIndex: 9999
                            });
                        }, i * 100);
                    }
                },

                // 2. Радужный водопад
                () => {
                    const colors = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3'];
                    for (let i = 0; i < 7; i++) {
                        setTimeout(() => {
                            myConfetti({
                                particleCount: 50,
                                spread: 60,
                                startVelocity: 30 + i * 5,
                                scalar: 1.2,
                                shapes: ['star', 'circle'],
                                colors: [colors[i], colors[(i + 1) % 7]],
                                origin: { x: 0.1 + (i * 0.12), y: 0.1 },
                                ticks: 100 + i * 10,
                                zIndex: 9999,
                                gravity: 0.8
                            });
                        }, i * 150);
                    }
                },

                // 3. Спиральный вихрь
                () => {
                    let spiralAngle = 0;
                    const spiralInterval = setInterval(() => {
                        spiralAngle += 0.5;
                        const x = 0.5 + Math.cos(spiralAngle) * 0.3;
                        const y = 0.5 + Math.sin(spiralAngle) * 0.3;
                        
                        myConfetti({
                            particleCount: 15,
                            spread: 20,
                            startVelocity: 20,
                            scalar: 0.8,
                            shapes: ['star', 'square', 'circle'],
                            colors: ['#9C27B0', '#E91E63', '#2196F3', '#00BCD4'],
                            origin: { x, y },
                            ticks: 60,
                            zIndex: 9999
                        });
                    }, 50);
                    
                    setTimeout(() => {
                        clearInterval(spiralInterval);
                        // Финальный взрыв в центре
                        myConfetti({
                            particleCount: 150,
                            spread: 150,
                            startVelocity: 45,
                            shapes: ['star', 'circle', 'square'],
                            colors: ['#9C27B0', '#E91E63', '#2196F3', '#00BCD4'],
                            origin: { x: 0.5, y: 0.5 },
                            ticks: 100,
                            zIndex: 9999
                        });
                    }, 2000);
                },

                // 4. Фейерверк-фонтан
                () => {
                    const fountainInterval = setInterval(() => {
                        myConfetti({
                            particleCount: 10,
                            spread: 30,
                            startVelocity: 60,
                            scalar: randomInRange(0.5, 1),
                            shapes: ['circle', 'square', 'star'],
                            colors: ['#26C6DA', '#42A5F5', '#5C6BC0', '#7E57C2'],
                            origin: { x: 0.5, y: 0.9 },
                            ticks: 100,
                            zIndex: 9999,
                            gravity: 2
                        });
                    }, 30);
                    
                    // Боковые фонтаны
                    setTimeout(() => {
                        const sideFountain1 = setInterval(() => {
                            myConfetti({
                                particleCount: 8,
                                spread: 20,
                                startVelocity: 40,
                                shapes: ['circle', 'square'],
                                colors: ['#FF6B6B', '#FFAB91'],
                                origin: { x: 0.2, y: 0.9 },
                                gravity: 1.5
                            });
                        }, 40);
                        
                        const sideFountain2 = setInterval(() => {
                            myConfetti({
                                particleCount: 8,
                                spread: 20,
                                startVelocity: 40,
                                shapes: ['star', 'circle'],
                                colors: ['#81C784', '#AED581'],
                                origin: { x: 0.8, y: 0.9 },
                                gravity: 1.5
                            });
                        }, 40);
                        
                        setTimeout(() => {
                            clearInterval(sideFountain1);
                            clearInterval(sideFountain2);
                        }, 1500);
                    }, 500);
                    
                    setTimeout(() => {
                        clearInterval(fountainInterval);
                    }, 2500);
                },

                // 5. Звездопад
                () => {
                    const secureRandom = () => {
                        const array = new Uint32Array(1);
                        crypto.getRandomValues(array);
                        return array[0] / (0xFFFFFFFF + 1);
                        };
                    // Звезды падают сверху
                    for (let i = 0; i < 30; i++) {
                        setTimeout(() => {
                            myConfetti({
                                particleCount: 5,
                                spread: 5,
                                startVelocity: 0,
                                scalar: randomInRange(0.8, 1.5),
                                shapes: ['star', 'circle', 'square'],
                                colors: ['#FFD700', '#FFEB3B', '#FFF9C4', '#FFFFFF'],
                                origin: { x: secureRandom(), y: -0.1 },
                                ticks: 300,
                                zIndex: 9999,
                                gravity: randomInRange(0.4, 0.6),
                                drift: randomInRange(-0.4, 0.4)
                            });
                        }, i * 100);
                    }
                    
                    // Взрывы при падении
                    setTimeout(() => {
                        for (let i = 0; i < 5; i++) {
                            setTimeout(() => {
                                myConfetti({
                                    particleCount: 30,
                                    spread: 70,
                                    startVelocity: 25,
                                    shapes: ['star', 'circle', 'square'],
                                    colors: ['#FFD700', '#FFEB3B', '#FFF9C4'],
                                    origin: { x: randomInRange(0.2, 0.8), y: randomInRange(0.7, 0.9) },
                                    ticks: 60,
                                    zIndex: 9999
                                });
                            }, i * 300);
                        }
                    }, 1000);
                },

                // 6. Цветочный букет
                () => {
                    // Центральный цветок
                    myConfetti({
                        particleCount: 100,
                        spread: 70,
                        startVelocity: 30,
                        scalar: 1.2,
                        shapes: ['circle', 'square', 'star'],
                        colors: ['#E91E63', '#F06292', '#F8BBD0'],
                        origin: { x: 0.5, y: 0.5 },
                        ticks: 80,
                        zIndex: 9999
                    });
                    
                    // Лепестки вокруг
                    for (let i = 0; i < 6; i++) {
                        setTimeout(() => {
                            const angle = (i / 6) * Math.PI * 2;
                            myConfetti({
                                particleCount: 40,
                                spread: 40,
                                startVelocity: 25,
                                scalar: 1,
                                shapes: ['circle', 'square', 'star'],
                                colors: ['#4CAF50', '#8BC34A', '#C8E6C9'],
                                origin: { 
                                    x: 0.5 + Math.cos(angle) * 0.25, 
                                    y: 0.5 + Math.sin(angle) * 0.25 
                                },
                                ticks: 70,
                                zIndex: 9999
                            });
                        }, 200 + i * 100);
                    }
                    
                    // Блестки
                    setTimeout(() => {
                        myConfetti({
                            particleCount: 50,
                            spread: 180,
                            startVelocity: 15,
                            scalar: 0.5,
                            shapes: ['star', 'square', 'circle'],
                            colors: ['#FFEB3B', '#FFF176', '#FFF59D'],
                            origin: { x: 0.5, y: 0.5 },
                            ticks: 100,
                            zIndex: 9999
                        });
                    }, 1000);
                },

                // 7. Космический взрыв
                () => {
                    // Центральная супернова
                    myConfetti({
                        particleCount: 200,
                        spread: 360,
                        startVelocity: 30,
                        scalar: 1,
                        shapes: ['star', 'circle', 'square'],
                        colors: ['#3F51B5', '#7986CB', '#C5CAE9', '#FFFFFF'],
                        origin: { x: 0.5, y: 0.5 },
                        ticks: 100,
                        zIndex: 9999
                    });
                    
                    // Планеты вокруг
                    const secureRandom = () => {
                        const array = new Uint32Array(1);
                        crypto.getRandomValues(array);
                        return array[0] / (0xFFFFFFFF + 1);
                        };
                        
                    for (let i = 0; i < 8; i++) {
                        setTimeout(() => {
                            const angle = (i / 8) * Math.PI * 2;
                            const distance = 0.2 + secureRandom() * 0.2;
                            myConfetti({
                                particleCount: 30,
                                spread: 30,
                                startVelocity: 20,
                                scalar: 0.8,
                                shapes: ['circle', 'star', 'square'],
                                colors: ['#FF5722', '#FFC107', '#4CAF50', '#2196F3', '#9C27B0'],
                                origin: { 
                                    x: 0.5 + Math.cos(angle) * distance, 
                                    y: 0.5 + Math.sin(angle) * distance 
                                },
                                ticks: 80,
                                zIndex: 9999
                            });
                        }, 300 + i * 50);
                    }
                    
                    // Космическая пыль
                    setTimeout(() => {
                        const dustInterval = setInterval(() => {
                            myConfetti({
                                particleCount: 2,
                                spread: 360,
                                startVelocity: 5,
                                scalar: randomInRange(0.2, 0.5),
                                shapes: ['circle', 'star'],
                                colors: ['#E3F2FD', '#C5CAE9', '#9FA8DA'],
                                origin: { x: randomInRange(0.1, 0.9), y: randomInRange(0.1, 0.9) },
                                ticks: 200,
                                zIndex: 9999,
                                drift: randomInRange(-0.2, 0.2)
                            });
                        }, 100);
                        
                        setTimeout(() => {
                            clearInterval(dustInterval);
                        }, 1500);
                    }, 1000);
                }
            ];

            const secureRandom = () => {
                const array = new Uint32Array(1);
                crypto.getRandomValues(array);
                return array[0] / (0xFFFFFFFF + 1);
                };
            // Выбираем случайный фейерверк
            const randomFirework = fireworkTypes[Math.floor(secureRandom() * fireworkTypes.length)];
            randomFirework();
        }
    };

    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.CHOICE_IMAGE,
        drop: (item: { src: string; index: number }) => {
            console.log("DROP: ", item);
            // const blockIndex = Number(oneGameState?.data?.selectedBlock) - 1;
            // const isCorrectDrop = item.index === oneGameState?.data?.correctImage && blockIndex === 1;
            const isCorrectDrop = item.index === oneGameState?.data?.correctImage;
            if (isCorrectDrop) {
                setDroppedImage(item.src);
                if (!isMuted && correctSoundRef.current) {
                    correctSoundRef.current.play();
                }
                triggerFireworkConfetti();
                // Переход на следующую игру через 3 секунды
                setTimeout(() => goToNextGame(), 3000);
            } else {
                message.error(t("tryAgain"));
                if (!isMuted && wrongSoundRef.current) {
                    wrongSoundRef.current.play();
                }
                setDroppedImage(null);
            }
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    });
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const [selectedPiece, setSelectedPiece] = useState(null);

    useEffect(() => {
        if (currentGame) {
            // Сбрасываем все состояния игры при смене
            setDroppedImage(null);
            setShowModal(false);
            setCounter(5);
            setHasPlayedAudio(false);
            setPlacedPieces([]);
            setAvailablePieces([]);
            setSelectedPiece(null);
            
            // Загружаем новую игру
            dispatch(fetchOneGameAction({ id: currentGame }));
        }
        // Загружаем список игр для навигации
        if (gameCategory) {
            const searchParams = new URLSearchParams(window.location.search);
            const age = searchParams.get("age");
            if (age) {
                const [fromAge, toAge] = age.split('-');
                dispatch(fetchGamesListAction({
                    category: gameCategory,
                    fromAge,
                    toAge
                }));
            }
        }
    }, [currentGame, gameCategory, dispatch]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileDevice(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Предотвращаем скролл на мобильных устройствах в режиме игры
    // useEffect(() => {
    //     if (isMobileDevice) {
    //         document.body.style.overflow = 'hidden';
    //         document.body.style.position = 'fixed';
    //         document.body.style.width = '100%';
    //         document.body.style.height = '100%';
            
    //         return () => {
    //             document.body.style.overflow = '';
    //             document.body.style.position = '';
    //             document.body.style.width = '';
    //             document.body.style.height = '';
    //         };
    //     }
    // }, [isMobileDevice]);

    const secureRandom = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] / (0xFFFFFFFF + 1);
    };

    const backgroundImage = useMemo(() => {
        if (oneGameState?.data?.backgrounds?.length) {
            const randomBg = oneGameState.data.backgrounds[Math.floor(secureRandom() * oneGameState.data.backgrounds.length)];
            return `url(${randomBg})`;
        }
        return "none";
    }, [oneGameState?.data?.backgrounds]);

    const renderCongratsText = useMemo(() => {
        return congratsText[Math.floor(secureRandom() * congratsText.length)];
    }, [showModal, congratsText]);

    const handleImageSelect = (index: number) => {
        if (index === oneGameState?.data?.correctImage) {
            if (!isMuted && correctSoundRef.current) {
                correctSoundRef.current.play();
            }
            triggerFireworkConfetti();
            // Переход на следующую игру через 3 секунды
            setTimeout(() => goToNextGame(), 3000);
        } else {
            message.error(t("tryAgain"));
            if (!isMuted && wrongSoundRef.current) {
                wrongSoundRef.current.play();
            }
        }
    };


    const renderGame = () => {
        const taskImages = oneGameState?.data?.taskImages || [];
        const isTaskImagesEmpty = taskImages.length === 0;

        const taskImagesWithPlaceholder = [];

        for (let i = 0; i < taskImages.length + 1; i++) {
            const position = i + 1;
            if (position === Number(oneGameState?.data?.selectedBlock)) {
                taskImagesWithPlaceholder.push(
                    <div
                        key={`placeholder-${i}`}
                         onClick={() => {
                            if (selectedChoice) {
                                const isCorrect = selectedChoice.index === oneGameState?.data?.correctImage;
                                if (isCorrect) {
                                    setDroppedImage(selectedChoice.src);
                                    if (!isMuted && correctSoundRef.current) {
                                        correctSoundRef.current.play();
                                    }
                                    triggerFireworkConfetti();
                                    setTimeout(() => goToNextGame(), 3000);
                                } else {
                                    message.error(t("tryAgain"));
                                    if (!isMuted && wrongSoundRef.current) {
                                        wrongSoundRef.current.play();
                                    }
                                    setDroppedImage(null);
                                }
                                setSelectedChoice(null); 
                            }
                        }}
                        ref={drop}
                        className={`placeholder ${isOver ? "hover" : ""}`}
                        style={{
                            width: screens.xs ? '130px' : "230px",
                            height: screens.xs ? '100px' : "200px",
                            border: "2px dashed #ccc",
                            display: "inline-block",
                            margin: "0",
                            // touchAction: "none",
                            userSelect: "none",
                            boxSizing: "border-box",
                        }}
                    >
                        {droppedImage && (
                            <Image
                                src={droppedImage}
                                preview={false}
                                width="100%"
                                height="100%"
                                style={{ 
                                    boxSizing: "border-box",
                                    objectFit: "contain"
                                }}
                            />
                        )}
                    </div>
                );
            } else {
                const imageIndex = position > Number(oneGameState?.data?.selectedBlock) ? i - 1 : i;
                taskImagesWithPlaceholder.push(
                    <Image
                        key={`taskImage-${i}`}
                        src={taskImages[imageIndex]}
                        preview={false}
                        width={screens.xs ? 130 : 230}
                        height={screens.xs ? 100 : 200}
                        style={{ margin: "0", boxSizing: "border-box" }}
                    />
                );
            }
        }

        const contentByEngine = () => {
            
            switch (oneGameState?.data?.engine) {
                case "dragGame":
                    return (
                        <div
                            className="game-body"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                justifyContent: isMobileDevice ? "flex-start" : "space-between",
                                alignItems: "center",
                                boxSizing: "border-box",
                                padding: isMobileDevice ? "20px 16px" : "0",
                                gap: isMobileDevice ? "20px" : "0",
                            }}
                        >
                            <Row className="task-images" justify="center" style={{ 
                                marginBottom: isMobileDevice ? 0 : 20,
                                width: "100%",
                                flexShrink: 0
                            }}>
                                {taskImagesWithPlaceholder}
                            </Row>
                            <Row className="choice-images" justify="center" style={{
                                width: "100%",
                                flexShrink: 0
                            }}>
                                {oneGameState?.data?.choiceImages?.map((src: any, index: any) => (
                                     <ChoiceImage
                                        key={index + "choice-image"}
                                        src={src}
                                        index={index + 1}
                                        isSelected={selectedChoice?.index === index + 1}
                                        onSelect={setSelectedChoice}
                                    />
                                ))}
                            </Row>
                        </div>
                    );
                case "selectGame":
                    return (
                        <Row className="game-body" align="middle" style={{ flexDirection: "column", height: "100%" }}>
                            <div
                            className="task-images"
                            style={{
                                display: !oneGameState?.data?.taskImages?.length ? 'none' : 'flex',
                                // width: oneGameState?.data?.taskImages?.length
                                // ? `${(isMobile ? 210 : 400) * oneGameState.data.taskImages.length}px`
                                // : '0px',
                                width: "auto",
                                height: isMobile ? '210px' : '400px',
                            }}
                            >
                                {taskImages.map((src, i) => (
                                    <Image key={i} src={src} preview={false} width="100%" height="100%" />
                                ))}
                            </div>
                            <div className="choice-images" style={{ marginTop: 42 }}>
                                {oneGameState?.data?.choiceImages?.map((src: string, index: number) => (
                                    <Button
                                        style={{ height: screens.xs ? 110 : 200, width: screens.xs ? 110 : 230 }}
                                        key={index}
                                        type="text"
                                        onClick={() => handleImageSelect(index + 1)}
                                    >
                                        <Image 
                                            src={src} 
                                            preview={false} 
                                            width="100%"
                                            height="100%"
                                            style={{
                                                objectFit: "contain"
                                            }}
                                        />
                                    </Button>
                                ))}
                            </div>
                        </Row>
                    );
                case "dropGame":
                    return (
                        <div
                            className="game-body"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                justifyContent: isMobileDevice ? "flex-start" : "space-between",
                                alignItems: "center",
                                boxSizing: "border-box",
                                padding: isMobileDevice ? "20px 16px" : "0",
                                gap: isMobileDevice ? "20px" : "0",
                            }}
                        >
                            <Row className="task-images" justify="center" style={{ 
                                marginBottom: isMobileDevice ? 0 : 20,
                                width: "100%",
                                flexShrink: 0
                            }}>
                                {taskImagesWithPlaceholder}
                            </Row>
                            <Row className="choice-images" justify="center" style={{
                                width: "100%",
                                flexShrink: 0
                            }}>
                                {oneGameState?.data?.choiceImages?.map((src: string, index: number) => (
                                    <ChoiceImage
                                        key={index + "choice-image"}
                                        src={src}
                                        index={index + 1}
                                        isSelected={selectedChoice?.index === index + 1}
                                        onSelect={setSelectedChoice}
                                    />
                                ))}
                            </Row>
                        </div>
                    );
                case "puzzleGame": {
                    const border = 12;
                    const scaleFactor = window.innerWidth < 768 ? 0.45 : 0.75;
                    const containerWidth = oneGameState.data.width * scaleFactor + border * 2;
                    const containerHeight = oneGameState.data.height * scaleFactor + border * 2;
                    const targets = oneGameState.data.choiceImages;
                    
                    // Mobile instructions - removed

                    // Функция для тактильного эффекта при правильном размещении
                    const triggerTactileEffect = (index: number) => {
                        // Тактильная обратная связь (вибрация на мобильных устройствах)
                        if (navigator.vibrate && isMobileDevice) {
                            navigator.vibrate(50);
                        }
                    };

                    return (
                         <div
                            style={{
                            flex: 1,
                            overflowY: "auto",
                            width: "100%",
                            height: "100%",
                            padding: 16,
                            boxSizing: "border-box",
                            }}
                        >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, minHeight: "100%",  }}>
                            <div
                                style={{
                                    position: "relative",
                                    width: containerWidth,
                                    height: containerHeight,
                                    border: "12px dashed rebeccapurple",
                                    borderRadius: 8,
                                    flexShrink: 0,
                                    backgroundColor: "transparent",
                                }}
                            >
                                {targets.map((target: { id: number; coord: { x: number; y: number }; url: string }, index: number) => {
                                    // Сначала проверяем в доступных элементах, потом в размещенных
                                    let pieceWithSize = availablePieces.find(p => p.id === target.id) || placedPieces[index];
                                    
                                    // Если не нашли, используем данные из исходного массива с масштабированием
                                    if (!pieceWithSize) {
                                        const originalPiece = oneGameState.data.choiceImages.find((p: any) => p.id === target.id);
                                        if (originalPiece) {
                                            pieceWithSize = {
                                                ...originalPiece,
                                                width: 130 * scaleFactor,
                                                height: 100 * scaleFactor,
                                                coord: {
                                                    x: originalPiece.coord.x * scaleFactor,
                                                    y: originalPiece.coord.y * scaleFactor
                                                }
                                            };
                                        }
                                    }
                                    
                                    if (!pieceWithSize) return null;
                                    
                                    const scaledTarget = {
                                        ...target,
                                        coord: pieceWithSize.coord || {
                                            x: target.coord.x * scaleFactor,
                                            y: target.coord.y * scaleFactor
                                        },
                                        width: pieceWithSize.width || 130 * scaleFactor,
                                        height: pieceWithSize.height || 100 * scaleFactor,
                                    };
                                    return (
                                        <React.Fragment key={`cell-${target.id}`}>
                                            <PuzzleCell
                                                target={scaledTarget}
                                                placedPiece={placedPieces[index]}
                                                isTargetCell={isMobileDevice && selectedPiece?.id === target.id}
                                                onDropPiece={(item: { id: number; url: string; width?: number; height?: number }) => {
                                                    if (item.id === target.id) {
                                                        const newPlaced = [...placedPieces];
                                                        newPlaced[index] = {
                                                            ...item,
                                                            width: pieceWithSize.width,
                                                            height: pieceWithSize.height
                                                        };
                                                        setPlacedPieces(newPlaced);
                                                        setAvailablePieces((prev: any[]) => prev.filter((p) => p.id !== item.id));
                                                        
                                                        // Тактильный эффект при правильном размещении
                                                        triggerTactileEffect(index);
                                                        
                                                        if (newPlaced.every((piece) => piece !== null)) {
                                                            if (!isMuted && correctSoundRef.current) {
                                                                correctSoundRef.current.play();
                                                            }
                                                            triggerFireworkConfetti();
                                                            // Переход на следующую игру через 3 секунды
                                                            setTimeout(() => goToNextGame(), 3000);
                                                        }
                                                    } else {
                                                        message.error(t("tryAgain"));
                                                        if (!isMuted && wrongSoundRef.current) {
                                                            wrongSoundRef.current.play();
                                                        }
                                                    }
                                                }}
                                                onClick={
                                                    isMobileDevice
                                                        ? () => {
                                                            if (selectedPiece) {
                                                                if (selectedPiece.id === target.id) {
                                                                    const newPlaced = [...placedPieces];
                                                                    newPlaced[index] = {
                                                                        id: selectedPiece.id,
                                                                        url: selectedPiece.url,
                                                                        width: pieceWithSize.width,
                                                                        height: pieceWithSize.height
                                                                    };
                                                                    setPlacedPieces(newPlaced);
                                                                    setAvailablePieces((prev) => prev.filter(p => p.id !== selectedPiece.id));
                                                                    setSelectedPiece(null);
                                                                    
                                                                    // Тактильный эффект при правильном размещении
                                                                    triggerTactileEffect(index);
                                                                    
                                                                    // Проверка на завершение игры
                                                                    if (newPlaced.every((p) => p !== null)) {
                                                                        if (!isMuted && correctSoundRef.current) correctSoundRef.current.play();
                                                                        triggerFireworkConfetti();
                                                                        // Переход на следующую игру через 3 секунды
                                                                        setTimeout(() => goToNextGame(), 3000);
                                                                    }
                                                                } else {
                                                                    message.error(t("tryAgain"));
                                                                    if (!isMuted && wrongSoundRef.current) wrongSoundRef.current.play();
                                                                    setSelectedPiece(null);
                                                                }
                                                            }
                                                        }
                                                        : undefined
                                                }
                                            />
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: isMobileDevice ? "wrap" : "nowrap",
                                    justifyContent: "center",
                                    gap: 10,
                                    maxWidth: isMobileDevice ? "100%" : "100%",
                                    padding: isMobileDevice ? "10px" : "20px 10px",
                                    backgroundColor: "transparent",
                                    borderRadius: 8,
                                    minHeight: 100,
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                {availablePieces.map((piece: { id: number; url: string; width?: number; height?: number }) => (
                                    <PuzzlePiece
                                        key={piece.id}
                                        piece={piece}
                                        onSelect={isMobileDevice ? setSelectedPiece : undefined}
                                        isSelected={isMobileDevice && selectedPiece?.id === piece.id}
                                    />
                                ))}
                            </div>
                        </div>
                        </div>
                    );
                }
                default:
                    return (
                        <Row justify="center" align="middle" style={{ height: "100%" }}>
                            {/* <MyText weight={700} size={32}>
                                В разработке
                            </MyText> */}
                        </Row>
                    );
            }
        };

        if (!oneGameState?.data) {
            return <Spin spinning={true} />;
        }

        if (isMobileDevice) {
            // Мобильная версия с эффектом полноэкранной модалки
            return (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundImage: backgroundImage || "none",
                    backgroundColor: backgroundImage ? "transparent" : "#f0f2f5",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    // overflowY: "auto",
                    zIndex: 9999,
                    backdropFilter: "blur(5px)",
                    animation: "slideUpModal 0.3s ease-out",
                }}>
                    {/* Красивый заголовок для мобильной версии */}
                    <header 
                        className="ant-layout-header"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            padding: "16px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            minHeight: "auto",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                        }}
                    >
                        <div 
                            style={{ 
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                flexWrap: "nowrap",
                                gap: "12px"
                            }}
                        >
                            <LogoIcon style={{ 
                                width: 140, 
                                height: 28, 
                                flexShrink: 0,
                                filter: "brightness(0) invert(1)"
                            }} />
                            <CloseOutlined 
                                onClick={() => {
                                    const searchParams = new URLSearchParams(window.location.search);
                                    // const age = searchParams.get("age");
                                    const ageParam = age ? `?age=${age}` : '';
                                    embeddedNavigate(`/materials/detail/games-list/${gameCategory}?age=${age}&type=lessons&selectedMaterial=${selectedMaterial}`);
                                }} 
                                style={{ 
                                    fontSize: 20, 
                                    padding: 8,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    color: "white",
                                    background: "rgba(255, 255, 255, 0.2)",
                                    borderRadius: "50%",
                                    backdropFilter: "blur(10px)",
                                    minWidth: "36px",
                                    minHeight: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }} 
                            />
                        </div>
                    </header>

                    {/* Название игры под заголовком */}
                    <div style={{
                        background: "transparent",
                        padding: "8px 16px 4px",
                        margin: "0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}>
                        <MyText size={18} weight={600} style={{ 
                            textAlign: "center", 
                            color: "#667eea",
                            margin: 0
                        }}>
                            {oneGameState?.data?.title}
                        </MyText>
                    </div>

                    {/* Инструкция без бордера */}
                    <div style={{
                        background: "transparent",
                        padding: "4px 16px 12px",
                        margin: "0 16px",
                        borderRadius: 8,
                    }}>
                        <MyText size={14} style={{ textAlign: "center" }}>
                            {oneGameState?.data?.task_text}
                        </MyText>
                    </div>

                    {/* Игровая область */}
                    <div style={{
                        flex: 1,
                        padding: "16px",
                        // display: "flex",
                        minHeight: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        overflowY: 'auto',
                        height: "100%",
                        WebkitOverflowScrolling: "touch",
                    }}>
                        {contentByEngine()}
                    </div>

                    {/* Аудио элементы и кнопка */}
                    <audio key={`audio-${currentGame}`} ref={audioRef} src={oneGameState?.data?.audio} preload="auto" />
                    <audio ref={correctSoundRef} src={correctSoundUrl} preload="auto" />
                    <audio ref={wrongSoundRef} src={wrongSoundUrl} preload="auto" />
                    
                    <Button
                        type="text"
                        shape="circle"
                        size="medium"
                        onClick={startAudio} 
                        style={{ 
                            position: "absolute",
                            bottom: 16,
                            right: 16,
                            width: 48,
                            height: 48,
                            background: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(6px)",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#667eea",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                        }}
                    >
                        {isMuted ? (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 9L20 5M20 9L16 5M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        ) : (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19.07 4.93C20.9445 6.80528 21.9982 9.34836 21.9982 12C21.9982 14.6516 20.9445 17.1947 19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </Button>
                </div>
            );
        }

        // Десктопная версия (оставляем как было)
        return (
            <>
            <Col span={24}>
            
                <Card style={{ borderRadius: 20, padding: "5px 0", boxSizing: "border-box" }}>
                    <Row >
                        <Col span={screens.xs ? 3 : 1}>
                        <ButtonContainer onClick={handleGoBack}>
                            <LeftOutlined style={{color: 'rgba(10, 3, 60, 0.7)'}}/>
                        </ButtonContainer>
                        </Col>
                        <Col span={screens.xs ? 21 : 23}>
                        <Row style={{ flexDirection: "column", boxSizing: "border-box" }}>
                        <MyText weight={700} size={20} style={{ marginBottom: 8, boxSizing: "border-box" }}>
                            {oneGameState?.data?.title}
                        </MyText>
                        <MyText size={14} style={{ boxSizing: "border-box" }}>
                            {oneGameState?.data?.task_text}
                        </MyText>
                        </Row>
                        </Col>

                    </Row>
                </Card>

                <div
                    className="game"
                    style={{
                        backgroundImage: backgroundImage || "none",
                        padding: "20px 0",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                  <Col span={24}>
                    {contentByEngine()}
                    </Col>

                </div>
                </Col>

            </>
        );
    };

    if (isMobileDevice) {
        // Для мобильной версии возвращаем только игру и модалки
        return (
            <>
                {/* CSS анимация для эффекта модалки */}
                <style>{`
                    @keyframes slideUpModal {
                        from {
                            opacity: 0;
                            transform: translateY(100%);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}</style>
                <canvas
                    id="confetti-canvas"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 10000,
                    }}
                />
                <DndProvider key={`game-${currentGame}`} backend={getDndBackend()} options={getDndOptions()}>
                    {renderGame()}
                </DndProvider>
                <Modal
                    open={showModal}
                    onCancel={handleCloseModal}
                    onOk={handleCloseModal}
                    footer={() => (
                        <Row justify="center">
                            <MyButton type="primary" onClick={handleCloseModal}>
                                {t("close")}
                            </MyButton>
                        </Row>
                    )}
                >
                    <Row style={{ marginTop: 28, marginBottom: 28 }} justify="center">
                        <MyText style={{ textAlign: "center" }} size={16} weight={500}>
                            {renderCongratsText}
                        </MyText>
                    </Row>
                </Modal>
            </>
        );
    }

    // Десктопная версия
    return (
    <>
        <Helmet>
          <title>
            Развивающие игры для детей раннего и дошкольного возраста – логика, внимание, память, подготовка к школе
          </title>
          <meta
            name="description"
            content="Онлайн-игры для детей раннего и дошкольного возраста: развитие логики, памяти, внимания и мышления. Игры, которые помогают родителям и педагогам в обучении и подготовке к школе."
          />
          <meta
            name="keywords"
            content="развивающие игры, игры для детей, игры для дошкольников, игры для малышей, логика, внимание, память, математика, подготовка к школе, развитие ребёнка"
          />
        </Helmet>

      <Row justify="center" style={{ boxSizing: "border-box" }}>
        <Col span={20}>
            <canvas
                id="confetti-canvas"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 9999,
                    boxSizing: "border-box",
                }}
            />
            <Col xs={24} sm={20} md={16} lg={24} style={{ boxSizing: "border-box" }}>
                <DndProvider key={`game-${currentGame}`} backend={getDndBackend()} options={getDndOptions()}>
                    <StyledGameContainer>
                        {renderGame()}
                        <div className="audio-container" style={{ boxSizing: "border-box", textAlign: "center", marginTop: 20 }}>
                            <Row justify="center" align="middle" style={{ boxSizing: "border-box" }}>
                                <audio key={`audio-${currentGame}`} ref={audioRef} src={oneGameState?.data?.audio} preload="auto" />
                                <audio ref={correctSoundRef} src={correctSoundUrl} preload="auto" />
                                <audio ref={wrongSoundRef} src={wrongSoundUrl} preload="auto" />
                                <MyButton type="text" 
                                onClick={startAudio} 
                                style={{ boxSizing: "border-box" }}>
                                    <img src={isMuted ? soundOffIcon : soundOnIcon} alt="sound toggle" style={{ width: 40, height: 40 }} />
                                </MyButton>
                            </Row>
                        </div>
                    </StyledGameContainer>
                </DndProvider>
            </Col>
            <Modal
                open={showModal}
                onCancel={handleCloseModal}
                onOk={handleCloseModal}
                footer={() => (
                    <Row justify="center" style={{ boxSizing: "border-box" }}>
                        <MyButton type="primary" onClick={handleCloseModal} style={{ fontSize: 14, boxSizing: "border-box" }}>
                            {t("close")}
                        </MyButton>
                    </Row>
                )}
            >
                <Row style={{ marginTop: 28, marginBottom: 28, boxSizing: "border-box" }} justify="center">
                    <MyText style={{ textAlign: "center", boxSizing: "border-box" }} size={16} weight={500}>
                        {renderCongratsText}
                    </MyText>
                </Row>
            </Modal>
            </Col>
            </Row>
    </>
    );
};